import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface SeriesPoint {
  key: string;
  value: number;
  date: string; // ISO date string
}

export interface Series {
  seriesLabel: string;
  seriesData: Array<SeriesPoint>;
}

export interface MultiLineChartProps {
  data: Array<Series>;
  dateFormat?: (date: string) => string;
  colors?: Array<string>;
  useTimeScale?: boolean; // Option to use accurate time scale for x-axis
  title?: string; // Chart title
  height?: number | `${number}%`; // Chart height
  testId?: string;
}

const defaultColors = ['#0000FF', '#8CBC94', '#F7A072', '#17301C', '#AD7A99'];

export const MultiLineChart = ({
  data,
  dateFormat = (date) => formatFHIRDate(date) || '',
  colors = defaultColors,
  useTimeScale = false,
  title,
  height,
  testId,
}: MultiLineChartProps) => {
  // Recharts needs a *flattened* data array with all series aligned by date
  const allDates = Array.from(
    new Set(data.flatMap((series) => series.seriesData.map((d) => d.date))),
  ).sort();

  const mergedData = allDates.map((date) => {
    const entry: Record<string, unknown> = { date };
    for (const series of data) {
      const point = series.seriesData.find((d) => d.date === date);
      // Include null for missing values so lines connect properly
      entry[series.seriesLabel] = point ? point.value : null;
    }
    return entry;
  });

  // If using time scale, convert dates to timestamps for accurate positioning
  const chartData = useTimeScale
    ? mergedData.map((entry) => ({
        ...entry,
        date: new Date(entry.date as string).getTime(),
      }))
    : mergedData;

  const timeDomain =
    useTimeScale && allDates.length > 0
      ? [new Date(allDates[0] ?? '').getTime(), new Date(allDates.at(-1) ?? '').getTime()]
      : undefined;

  // Calculate Y-axis domains for each series to make changes more visible
  const yAxisDomains = data.map((series) => {
    const values = series.seriesData.map((d) => d.value).filter((v) => v !== null);
    if (values.length === 0) {
      return undefined;
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;

    // Add 10% padding and ensure minimum range for visibility
    const padding = Math.max(range * 0.1, (max - min) * 0.05);
    return [min - padding, max + padding];
  });

  return (
    <div className={twx(typeof height === 'string' ? 'h-full' : undefined)} data-testid={testId}>
      {title && <div className={tw`mb-4 text-center text-lg`}>{title}</div>}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="4 4" horizontal={false} />
          <XAxis
            dataKey="date"
            tickFormatter={
              useTimeScale
                ? (timestamp: number) => dateFormat(new Date(timestamp).toISOString()) || ''
                : (value: string) => dateFormat(value) || ''
            }
            type={useTimeScale ? 'number' : 'category'}
            domain={timeDomain}
          />
          <Tooltip
            labelFormatter={
              useTimeScale
                ? (label) => dateFormat(new Date(Number(label)).toISOString())
                : (label) => dateFormat(String(label))
            }
          />
          {data.length > 1 && <Legend />}

          {data.map((series, idx) => (
            <YAxis
              key={series.seriesLabel}
              yAxisId={series.seriesLabel}
              orientation={idx % 2 === 0 ? 'left' : 'right'}
              stroke={colors[idx % colors.length]}
              tick={{
                fill: colors[idx % colors.length],
              }}
              axisLine={{ stroke: colors[idx % colors.length] }}
              tickLine={{ stroke: colors[idx % colors.length] }}
              domain={yAxisDomains[idx]}
              tickFormatter={(value) => {
                // Format numbers more cleanly
                if (value >= 1000) {
                  return `${(value / 1000).toFixed(1)}k`;
                }
                if (value >= 10) {
                  return value.toFixed(0);
                }
                return value.toFixed(1);
              }}
            />
          ))}

          {data.map((series, idx) => (
            <Line
              key={series.seriesLabel}
              type="linear"
              dataKey={series.seriesLabel}
              yAxisId={series.seriesLabel}
              stroke={colors[idx % colors.length]}
              dot={{ r: 3, fill: colors[idx % colors.length] }}
              activeDot={{ r: 5, fill: colors[idx % colors.length] }}
              connectNulls={true}
              name={series.seriesLabel}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
